import type { FC } from "react";
import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useOrderBookings } from "./useOrderBookings";
import { useProducts } from "../../admin/products/useProducts";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import type { OrderBookingResponse } from "../ordersApi";
import dayjs from "../../dayjs";

interface OrderBookingsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly bookings: ReadonlyArray<OrderBookingResponse>;
  readonly productNameById: ReadonlyMap<string, string>;
  readonly onCancel: (bookingId: string) => Promise<void>;
}

const OrderBookingsDialog: FC<OrderBookingsDialogProps> = ({ open, onClose, bookings, productNameById, onCancel }) => (
  <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
    <DialogTitle>Rezerwacje do zamówienia</DialogTitle>
    <DialogContent>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Data utworzenia</TableCell>
            <TableCell>Numer rezerwacji</TableCell>
            <TableCell>Produkt</TableCell>
            <TableCell>Potwierdzona</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.map((b) => (
            <TableRow key={b.id} hover>
              <TableCell>{dayjs(b.createdAt).format("DD.MM.YYYY HH:mm")}</TableCell>
              <TableCell>{b.bookingReference}</TableCell>
              <TableCell>{productNameById.get(b.productId) ?? b.productId}</TableCell>
              <TableCell>
                <Checkbox checked={!!b.isConfirmed} readOnly size="small" />
              </TableCell>
              <TableCell align="right">
                <AsyncLoadingButton variant="outlined" color="error" size="small" onClick={() => onCancel(b.id)}>
                  Anuluj
                </AsyncLoadingButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Zamknij</Button>
    </DialogActions>
  </Dialog>
);

interface Props {
  readonly orderId: string;
}

const OrderCancelBookingsButton: FC<Props> = ({ orderId }) => {
  const { bookings, isLoading, onCancel } = useOrderBookings(orderId);
  const { products } = useProducts();
  const [open, setOpen] = useState(false);

  const hasAnyBookings = !isLoading && bookings.length > 0;

  const productNameById = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [products]);

  if (!hasAnyBookings) {
    return null;
  }

  return (
    <>
      <Button variant="outlined" color="warning" onClick={() => setOpen(true)}>
        Anuluj rezerwację
      </Button>
      <OrderBookingsDialog
        open={open}
        onClose={() => setOpen(false)}
        bookings={bookings}
        productNameById={productNameById}
        onCancel={onCancel}
      />
    </>
  );
};

export default OrderCancelBookingsButton;
